// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 70b34e38-d715-4a94-ace5-e7f081cb7fdc

// ---------------------------------------------------------------------------
// Primordia — Survey Workflow
// ---------------------------------------------------------------------------
// Discovery's real bottleneck is not the CMDB, it is chasing application owners
// by email. This turns that chase into the product loop:
//
//   1. Campaigns  — scope a questionnaire to a set of apps + a few fact-sheet
//                   SECTIONS, assign it to EXISTING Primordia users of this
//                   workspace, send an in-app notification, then chase what
//                   is outstanding.
//   2. Respond    — the assignee's view. Only the scoped fields, friendly
//                   labels, helper text, a progress bar, save-draft, submit,
//                   and a "mark my part done" that tells the campaign (not
//                   just the fact sheet) that this person finished.
//   3. Outstanding— the chase list: which apps still lack which MANDATORY
//                   fields, grouped by the person who owns them.
//
// V1 talks to backend/lambda/surveys.js — GET/PUT/DELETE /surveys,
// PUT /surveys/assignments, POST /surveys/notify, PUT /surveys/respond — see
// backend/sql/017_surveys.sql for why recipients are EXISTING Primordia users
// only, and why there is deliberately no public, no-JWT "answer via emailed
// link" flow for someone with no account. When that API is not reachable
// (not deployed to this stage yet, or a network error) campaigns fall back to
// this browser's localStorage exactly as before, and the screen says so
// plainly — a consultant who believes a notification went out will not chase
// the recipient themselves, and the discovery stalls with nobody noticing.
// Tabs 2 and 3 work fully offline against the local workspace either way —
// they are the parts that create value regardless of which mode Campaigns is in.
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Button, Tabs, Tab, Table, TableHead, TableRow,
  TableCell, TableBody, TableSortLabel, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Alert, Checkbox, FormControlLabel,
  CircularProgress, Tooltip, IconButton, LinearProgress, Divider, Autocomplete,
  Accordion, AccordionSummary, AccordionDetails, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { getConfig } from './config';
import { getIdToken, getSession } from './auth';
import { store, completionOver } from './store';
import { allFieldsFor, resolvePicklist, FIELD_TYPES } from './schema';
import HelpBubble from './HelpBubble';
import { TabLabel } from './ui';

// ---- Optimum palette -------------------------------------------------------
const NAVY = '#021F4B';
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const HEAD = 'sectionHeader.main';
const headSx = { '& th': { fontWeight: 700, color: HEAD } };

const LS_SURVEYS = 'primordia.surveys';
const LS_DRAFTS = 'primordia.surveys.drafts';
const STATUS_OPTIONS = ['draft', 'open', 'closed'];

// ---- Schema-derived constants (the schema is static, so compute once) -------
const APP_FIELDS = allFieldsFor('Application').filter(
  (f) => f.type !== FIELD_TYPES.LIFECYCLE && f.type !== FIELD_TYPES.RELATION
);
const SECTION_NAMES = APP_FIELDS.reduce((acc, f) => (acc.includes(f.section) ? acc : acc.concat(f.section)), []);
const SECTION_SIZE = SECTION_NAMES.reduce((acc, s) => {
  acc[s] = APP_FIELDS.filter((f) => f.section === s).length;
  return acc;
}, {});
// One row per field key (hostingType / vendorName appear in two sections).
const UNIQUE_FIELDS = APP_FIELDS.filter((f, i) => APP_FIELDS.findIndex((x) => x.key === f.key) === i);
const MANDATORY_FIELDS = UNIQUE_FIELDS.filter((f) => f.mandatory);

const OWNER_FIELDS = [
  { key: 'applicationOwner', role: 'Business Owner' },
  { key: 'technicalOwner', role: 'Technical Owner' },
  { key: 'primaryStakeholder', role: 'Primary Stakeholder' },
];

// ---- Small helpers ---------------------------------------------------------
const isEmpty = (v) => v === undefined || v === null || v === '' || (Array.isArray(v) && !v.length);
const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || '').trim());
const uid = (p) => `${p}-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e4).toString(36)}`;
const shortDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '—');
const pct = (n, d) => (d ? Math.round((n / d) * 100) : 0);
// Rendered options per picker. Selection and enrichment always cover the full
// set; this only bounds what is mounted.
const PICKER_CAP = 250;

const readLocal = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (e) { return fallback; }
};
const writeLocal = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* quota */ }
};

// Fields in the selected sections, de-duplicated by key.
// `findIndex` inside `filter` made this O(F^2) on every call, and it is called
// from the render path and from `statsFor` for every campaign row.
const fieldsForSections = (sections) => {
  const wanted = new Set(sections || []);
  const seen = new Set();
  return APP_FIELDS.filter((f) => {
    if (!wanted.has(f.section) || seen.has(f.key)) return false;
    seen.add(f.key);
    return true;
  });
};

// The canonical scoping data for a campaign is its FIELD-KEY set — sections
// are a display/editing convenience derived from it. A campaign loaded from
// the API only ever carries fieldKeys (survey_campaign.field_keys); a
// campaign left over in localStorage from before this backend existed only
// ever carries sections. Both are handled here so neither shape has to be
// migrated or special-cased at every call site.
const fieldsForCampaign = (c) => {
  const keys = new Set(Array.isArray(c.fieldKeys) ? c.fieldKeys : []);
  if (keys.size) return UNIQUE_FIELDS.filter((f) => keys.has(f.key));
  return fieldsForSections(c.sections || []);
};
const sectionsForDisplay = (c) => {
  if (c.sections && c.sections.length) return c.sections;
  return Array.from(new Set(fieldsForCampaign(c).map((f) => f.section)));
};

// Plain-English helper text. Specific where it matters, generic otherwise —
// this is what makes the owner's form feel like a form and not a database.
const HELP = {
  description: 'A sentence or two a non-technical colleague would understand.',
  appPurpose: 'What does this application actually do for your team day to day?',
  applicationOwner: 'The business person accountable for this application.',
  technicalOwner: 'Who supports it technically — your team or a vendor?',
  primaryStakeholder: 'The person who feels it first when this system is down.',
  primaryStakeholderDirector: 'Their director — who we escalate to.',
  vendorName: 'The company that supplies the software.',
  vendorContact: 'Name of your day-to-day contact at the vendor.',
  vendorEmail: 'Vendor email address, so we can ask them the technical questions.',
  hostingType: 'Where it runs today: your data centre, a vendor cloud (SaaS), or cloud infrastructure.',
  hostingDescription: 'Anything unusual about how or where it is hosted.',
  rto: 'How long could you work without this system before it becomes a real problem?',
  rpo: 'How much recent data could you afford to re-enter after an outage?',
  phi: 'Does it hold patient data (PHI)?',
  patientSafetyImpact: 'If this is down, what is the risk to patient care?',
  downtimeProcedures: 'Do you have a documented paper or manual process for downtime?',
  remoteWork: 'Can your team use this from outside the hospital network?',
  ssoAvailability: 'Do users sign in with their normal network account?',
  latencySensitive: 'Does it feel slow or break if the network is slower?',
  hardcodedIps: 'Are there fixed IP addresses configured anywhere (devices, interfaces, scripts)?',
  normalHours: 'When is it normally used — e.g. Mon-Fri 7am-7pm, or 24/7.',
  peakPeriods: 'Busiest times: month-end, shift change, flu season…',
  mustWhitelist: 'Any addresses, URLs or ports that must be allowed through the firewall.',
  serverInventory: 'Server names if you know them — a rough list is fine.',
  lifecycleState: 'Is it being planned, live today, or on the way out?',
};
const GENERIC = {
  [FIELD_TYPES.TEXTAREA]: 'As much detail as you have — bullet points are fine.',
  [FIELD_TYPES.SELECT]: 'Pick the closest option.',
  [FIELD_TYPES.MULTISELECT]: 'Choose all that apply.',
  [FIELD_TYPES.BOOLEAN]: 'Yes or no — leave blank if you are not sure.',
  [FIELD_TYPES.NUMBER]: 'A number.',
  [FIELD_TYPES.CURRENCY]: 'Approximate annual cost in dollars.',
  [FIELD_TYPES.PERCENT]: 'A percentage from 0 to 100.',
  [FIELD_TYPES.DATE]: 'Pick a date.',
  [FIELD_TYPES.URL]: 'Full web address, starting with https://',
};
const helperFor = (f) => HELP[f.key] || GENERIC[f.type] || 'Short answer.';

const DEFAULT_MESSAGE =
  'We are documenting our application estate ahead of the cloud programme. ' +
  'You are listed as an owner for the applications below. The questions are short and ' +
  'scoped to your area — if you do not know an answer, leave it blank and we will chase it elsewhere. ' +
  'Thank you.';

// ---- Presentational bits ---------------------------------------------------
function Progress({ value, width = 130, showLabel = true }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <LinearProgress
        variant="determinate"
        value={v}
        sx={{
          width, height: 8, borderRadius: 4, bgcolor: '#E6ECF3',
          '& .MuiLinearProgress-bar': { bgcolor: v >= 100 ? '#2E7D32' : BLUE, borderRadius: 4 },
        }}
      />
      {showLabel && <Typography variant="caption" sx={{ color: GREY, minWidth: 34 }}>{v}%</Typography>}
    </Stack>
  );
}

function SectionChips({ sections, max = 2 }) {
  const shown = sections.slice(0, max);
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {shown.map((s) => <Chip key={s} size="small" label={s} variant="outlined" sx={{ height: 20 }} />)}
      {sections.length > max && (
        <Chip size="small" label={`+${sections.length - max}`} sx={{ height: 20, bgcolor: '#E6ECF3' }} />
      )}
    </Stack>
  );
}

const STATUS_COLOR = { draft: '#9AA6B2', open: '#0D5B9A', closed: '#5C6B7A', sent: '#5C6B7A' };

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function SurveyWorkflow({ tenantId }) {
  const [tab, setTab] = useState(0);
  const [apps, setApps] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [users, setUsers] = useState([]); // this tenant's Primordia users — assignee picker source
  const [loading, setLoading] = useState(true);
  const [localMode, setLocalMode] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  // campaign dialog (create AND edit — apiId distinguishes them)
  const [dialog, setDialog] = useState(null); // draft campaign or null
  const [emailEdits, setEmailEdits] = useState({});
  const [missingFieldKey, setMissingFieldKey] = useState('');

  // send-notifications confirmation
  const [sendDialog, setSendDialog] = useState(null); // { campaign, confirmText }
  const [sendBusy, setSendBusy] = useState(false);
  const [notifyResults, setNotifyResults] = useState(null); // { campaignId, campaignName, results, truncated }

  // respond tab
  const [respCampaignId, setRespCampaignId] = useState('');
  const [respAppId, setRespAppId] = useState('');
  const [respSections, setRespSections] = useState([]);
  const [respondent, setRespondent] = useState('');
  const [answers, setAnswers] = useState({});
  const [baseline, setBaseline] = useState({});

  // outstanding tab
  const [sortBy, setSortBy] = useState('missing');
  const [sortDir, setSortDir] = useState('desc');

  const me = getSession();

  const api = useCallback(async (path, options = {}) => {
    const res = await fetch(`${getConfig().apiUrl}${path}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getIdToken()}`, ...(options.headers || {}) },
    });
    const text = await res.text();
    // An error envelope is often an HTML page, not JSON. Parsing it bare threw
    // SyntaxError here — before the !res.ok check below could report the real
    // status — so a 502 surfaced as "Unexpected token <". Same guarded form as
    // AiEnrichment and RelationshipWizard.
    let data = {};
    try { data = text ? JSON.parse(text) : {}; } catch (e) { data = {}; }
    if (!res.ok) throw new Error(data.error || data.message || `request failed (${res.status})`);
    return data;
  }, []);

  const localCampaigns = useCallback(
    () => (readLocal(LS_SURVEYS, []) || []).filter((c) => !tenantId || c.tenantId === tenantId),
    [tenantId]
  );

  const saveLocalCampaigns = useCallback((next) => {
    const others = (readLocal(LS_SURVEYS, []) || []).filter((c) => (tenantId ? c.tenantId !== tenantId : false));
    writeLocal(LS_SURVEYS, [...others, ...next]);
  }, [tenantId]);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      setApps(await store.listFactSheets(tenantId, { type: 'Application' }));
    } catch (e) {
      setErr(e.message);
    }

    // Best-effort, and deliberately not awaited into the try/catch below: it
    // only feeds the assignee picker's "existing Primordia users" list.
    // GET /users is admin/master-only server-side (unchanged, untouched here)
    // — an editor simply sees an empty list and falls back to the manual
    // "add by email" field, same as UserAdmin's contacts fetch does for a
    // screen that lacks that permission.
    let dirUsers = [];
    try {
      const u = await api(`/users${tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : ''}`);
      if (Array.isArray(u)) dirUsers = u;
    } catch (e) { /* picker falls back to manual email entry */ }
    setUsers(dirUsers);
    const nameFor = (email) => {
      const u = dirUsers.find((x) => String(x.email || '').toLowerCase() === email);
      const full = u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() : '';
      return full || email;
    };

    const q = `?tenantId=${encodeURIComponent(tenantId || '')}`;
    try {
      const data = await api(`/surveys${q}`);
      const rows = Array.isArray(data) ? data : (data.campaigns || []);
      setCampaigns(rows.map((c) => {
        const fieldKeys = c.fieldKeys || [];
        const appIds = c.factSheetIds || [];
        return {
          id: c.id,
          apiId: c.id,
          tenantId: c.tenantId,
          name: c.name,
          fieldKeys,
          sections: sectionsForDisplay({ fieldKeys }),
          appIds,
          status: c.status,
          dueDate: c.dueDate,
          createdBy: c.createdBy,
          createdAt: c.createdAt,
          updatedAt: c.updatedAt,
          counts: c.counts || { total: 0, pending: 0, inProgress: 0, done: 0 },
          // Same shape the dialog and table already work with, decorated with
          // a display name from the directory when we have one loaded.
          recipients: (c.assignees || []).map((a) => ({
            key: a.email, email: a.email, name: nameFor(a.email), roles: [],
            appIds, assignmentStatus: a.status, notifiedAt: a.notifiedAt, completedAt: a.completedAt,
          })),
        };
      }));
      setLocalMode(false);
    } catch (e) {
      setLocalMode(true);
      setCampaigns(localCampaigns());
    }
    setLoading(false);
  }, [api, tenantId, localCampaigns]);

  useEffect(() => { load(); }, [load]);

  const appById = useMemo(() => {
    const m = {};
    apps.forEach((a) => { m[a.id] = a; });
    return m;
  }, [apps]);

  // ---- Recipient derivation ------------------------------------------------
  // Owners live on the fact sheets themselves. Some tenants store an email,
  // most store a person's name — we surface both and let the user fill the gap.
  const suggestedRecipients = useMemo(() => {
    const map = new Map();
    (dialog ? dialog.appIds : []).forEach((id) => {
      const app = appById[id];
      if (!app) return;
      OWNER_FIELDS.forEach(({ key, role }) => {
        const raw = app.fields ? app.fields[key] : '';
        const value = String(raw || '').trim();
        if (!value) return;
        const k = value.toLowerCase();
        const existing = map.get(k) || {
          key: k,
          name: isEmail(value) ? value.split('@')[0] : value,
          email: isEmail(value) ? value : '',
          roles: [],
          appIds: [],
        };
        if (!existing.roles.includes(role)) existing.roles.push(role);
        if (!existing.appIds.includes(id)) existing.appIds.push(id);
        map.set(k, existing);
      });
    });
    return Array.from(map.values()).sort((a, b) => b.appIds.length - a.appIds.length);
  }, [dialog, appById]);

  // Existing Primordia users of this workspace, for the assignee picker.
  // V1 can only notify someone with an account, so this is the list that
  // will actually be mailed — suggestedRecipients above is a shortlist to
  // pick FROM, not a guarantee any of them have one.
  const tenantUserOptions = useMemo(
    () => users
      .filter((u) => u.email)
      .map((u) => ({
        key: String(u.email).toLowerCase(),
        email: u.email,
        label: [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email,
      })),
    [users]
  );

  // ---- Campaign statistics -------------------------------------------------
  const statsFor = useCallback((c) => {
    const fields = fieldsForCampaign(c);
    // Scored with `completionOver`, the same rule the fact-sheet badge uses.
    const scored = (c.appIds || [])
      .map((id) => appById[id])
      .filter(Boolean)
      .map((app) => completionOver(fields, app.fields));
    const dataPct = scored.length
      ? Math.round(scored.reduce((a, b) => a + b, 0) / scored.length)
      : 0;
    if (c.counts) {
      // API mode — counts come straight off survey_assignment rows, so
      // "received" here means an assignee marked their part DONE, not merely
      // that they were notified.
      return {
        recipients: c.counts.total,
        received: c.counts.done,
        outstanding: c.counts.total - c.counts.done,
        responsePct: pct(c.counts.done, c.counts.total),
        dataPct,
        questions: fields.length,
      };
    }
    // Local-mode legacy shape — unchanged from before this backend existed.
    const recipients = c.recipients || [];
    const responded = new Set((c.responses || []).map((r) => String(r.email || r.name || '').toLowerCase()));
    const received = recipients.filter((r) => responded.has(String(r.email || r.name || '').toLowerCase())).length;
    const outstanding = Math.max(0, recipients.length - received);
    return {
      recipients: recipients.length,
      received,
      outstanding,
      responsePct: pct(received, recipients.length),
      dataPct,
      questions: fields.length,
    };
  }, [appById]);

  // ---- Create / edit / save -------------------------------------------------
  const openDialog = () => {
    setEmailEdits({}); setMissingFieldKey('');
    setDialog({
      id: uid('cmp'),
      apiId: null,
      tenantId: tenantId || '',
      name: '',
      appIds: [],
      sections: [],
      recipients: [],
      dueDate: '',
      status: 'open',
      message: DEFAULT_MESSAGE,
    });
  };

  // Editing is API-mode only — a local-only campaign never earned a real id
  // to update against, so the row action that opens this is hidden while
  // localMode is true (see the Campaigns table below).
  const openEditDialog = (c) => {
    setEmailEdits({}); setMissingFieldKey('');
    setDialog({
      id: c.id,
      apiId: c.apiId || c.id,
      tenantId: c.tenantId || tenantId || '',
      name: c.name,
      appIds: c.appIds || [],
      sections: c.sections || [],
      recipients: (c.recipients || []).map((r) => ({ ...r })),
      dueDate: c.dueDate || '',
      status: c.status || 'open',
      message: DEFAULT_MESSAGE,
    });
  };

  const setDraft = (patch) => setDialog((d) => ({ ...d, ...patch }));

  const toggleRecipient = (s) => {
    const email = emailEdits[s.key] !== undefined ? emailEdits[s.key] : s.email;
    setDialog((d) => {
      const on = (d.recipients || []).some((r) => r.key === s.key);
      const recipients = on
        ? d.recipients.filter((r) => r.key !== s.key)
        : d.recipients.concat([{ key: s.key, name: s.name, email, roles: s.roles, appIds: s.appIds }]);
      return { ...d, recipients };
    });
  };

  const setRecipientEmail = (key, email) => {
    setEmailEdits((m) => ({ ...m, [key]: email }));
    setDialog((d) => ({ ...d, recipients: (d.recipients || []).map((r) => (r.key === key ? { ...r, email } : r)) }));
  };

  const addManualRecipient = (raw) => {
    const email = String(raw || '').trim();
    if (!email) return;
    setDialog((d) => {
      if ((d.recipients || []).some((r) => r.key === email.toLowerCase())) return d;
      return {
        ...d,
        recipients: d.recipients.concat([{
          key: email.toLowerCase(), name: email.split('@')[0], email, roles: ['Added manually'], appIds: d.appIds,
        }]),
      };
    });
  };

  const addExistingUser = (opt) => {
    if (!opt) return;
    setDialog((d) => {
      if ((d.recipients || []).some((r) => r.key === opt.key)) return d;
      return {
        ...d,
        recipients: d.recipients.concat([{
          key: opt.key, name: opt.label, email: opt.email, roles: ['Primordia user'], appIds: d.appIds,
        }]),
      };
    });
  };

  const addAppsMissingField = () => {
    if (!missingFieldKey) return;
    const ids = apps.filter((a) => isEmpty(a.fields ? a.fields[missingFieldKey] : undefined)).map((a) => a.id);
    setDialog((d) => ({ ...d, appIds: Array.from(new Set(d.appIds.concat(ids))) }));
  };

  const saveCampaign = async () => {
    const draft = dialog;
    const fieldKeys = fieldsForSections(draft.sections).map((f) => f.key);
    setBusy(true); setErr(''); setMsg('');
    try {
      const payload = {
        ...(draft.apiId ? { id: draft.apiId } : {}),
        tenantId: draft.tenantId || tenantId,
        name: draft.name || 'Untitled campaign',
        fieldKeys,
        factSheetIds: draft.appIds,
        status: draft.status || 'open',
        dueDate: draft.dueDate || null,
      };
      const saved = await api('/surveys', { method: 'PUT', body: JSON.stringify(payload) });
      const campaignId = saved.id;
      const emails = (draft.recipients || []).map((r) => r.email).filter(Boolean);
      const recon = await api('/surveys/assignments', {
        method: 'PUT', body: JSON.stringify({ campaignId, emails }),
      }).catch(() => null);
      const skipped = (recon && recon.skipped) || [];
      setMsg(
        `Campaign "${payload.name}" saved. `
        + `${emails.length - skipped.length} of ${emails.length} recipient(s) assigned`
        + (skipped.length
          ? ` — ${skipped.length} skipped (no enabled Primordia account in this workspace): `
            + `${skipped.map((s) => s.email).join(', ')}.`
          : '.'),
      );
      setDialog(null);
      await load();
    } catch (e) {
      // Offline fallback — unchanged from before this backend existed: keep
      // the campaign in this browser and say plainly that nobody was emailed.
      const campaign = {
        ...draft,
        name: draft.name || 'Untitled campaign',
        createdAt: draft.createdAt || new Date().toISOString(),
        fieldKeys,
        status: 'sent',
        reminders: draft.reminders || 0,
        responses: draft.responses || [],
      };
      const next = campaigns.filter((x) => x.id !== campaign.id).concat([campaign]);
      setCampaigns(next);
      saveLocalCampaigns(next);
      setLocalMode(true);
      setMsg(`Campaign "${campaign.name}" saved in this browser — ${campaign.recipients.length} recipient(s), `
        + `${campaign.appIds.length} application(s). No invitation was emailed: contact the recipients yourself, `
        + `then record their answers on the Respond tab.`);
      setDialog(null);
    } finally {
      setBusy(false);
    }
  };

  // Row-level "notify" icon: API mode opens the real confirmation dialog;
  // local mode has nothing to send through, so it says so and stops there —
  // no network call is attempted, unlike the old /surveys/send path this
  // replaces.
  const openSendDialog = (c) => setSendDialog({ campaign: c, confirmText: '' });
  const notifyIconClick = (c) => {
    if (localMode) {
      setErr(''); setMsg('');
      setMsg(`No notification was emailed — "${c.name}" is stored only in this browser. `
        + `Contact its ${(c.recipients || []).length} recipient(s) yourself and record their answers on the Respond tab.`);
      return;
    }
    openSendDialog(c);
  };

  // THE confirmation. Requires typing the word SEND — the email non-negotiable
  // (CLAUDE.md) earns UI friction here on top of every server-side guard.
  const confirmSend = async () => {
    if (!sendDialog || sendDialog.confirmText.trim().toUpperCase() !== 'SEND') return;
    setSendBusy(true); setErr(''); setMsg('');
    try {
      const res = await api('/surveys/notify', {
        method: 'POST',
        body: JSON.stringify({ campaignId: sendDialog.campaign.id, confirmSend: true }),
      });
      const results = res.results || [];
      setNotifyResults({
        campaignId: sendDialog.campaign.id,
        campaignName: sendDialog.campaign.name,
        results,
        truncated: !!res.moreOutstanding,
      });
      const sent = results.filter((r) => r.status === 'sent').length;
      setMsg(`Sent ${sent} notification(s) for "${sendDialog.campaign.name}".`
        + (res.moreOutstanding ? ' More recipients are still outstanding beyond this run’s 50-recipient cap — send again to reach them.' : ''));
      setSendDialog(null);
      await load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSendBusy(false);
    }
  };

  // Outstanding-tab owner chase. This is intentionally informational only —
  // it is derived from fact-sheet owner FIELDS across every application, not
  // from a campaign's assignment list, so there is no assignee record and no
  // account check to send an in-app notification against. Scoping a real
  // Surveys campaign to an owner's applications and using Send notifications
  // there is the actual send path.
  const remindOwner = (owner) => {
    setErr(''); setMsg('');
    setMsg(`No reminder was emailed — outstanding-owner reminders here are informational only. `
      + `${owner.name}${owner.email ? ` (${owner.email})` : ' — no email on file'} has ${owner.missingCount} `
      + `answer(s) outstanding across ${owner.apps.length} application(s). To actually notify someone, scope a `
      + `Surveys campaign to their applications on the Campaigns tab and use Send notifications there.`);
  };

  // ---- Respond tab ---------------------------------------------------------
  const respondCampaign = campaigns.find((c) => c.id === respCampaignId) || null;
  const respondApp = appById[respAppId] || null;
  const respondFields = useMemo(() => fieldsForSections(respSections), [respSections]);

  // Pick up a campaign's scope, and prefill from what we already know.
  useEffect(() => {
    if (!respondCampaign) return;
    setRespSections(respondCampaign.sections || []);
    if (!respondCampaign.appIds.includes(respAppId)) setRespAppId(respondCampaign.appIds[0] || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [respCampaignId]);

  useEffect(() => {
    const app = appById[respAppId];
    if (!app) { setAnswers({}); return; }
    const drafts = readLocal(LS_DRAFTS, {}) || {};
    const saved = (drafts[respAppId] || {}).answers || {};
    // Remember what was already on file. The form is prefilled from the fact
    // sheet, so without a baseline every prefilled value counted as an answer
    // the respondent had just given — "Thank you, 14 answer(s) saved" after
    // someone typed one. A saved draft IS an answer, so the baseline is the
    // fact sheet alone.
    setBaseline({ ...(app.fields || {}) });
    setAnswers({ ...(app.fields || {}), ...saved });
    const owner = OWNER_FIELDS.map((o) => (app.fields || {})[o.key]).find(Boolean);
    setRespondent((prev) => prev || String(owner || ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [respAppId, apps]);

  const sameAsFile = (a, b) => JSON.stringify(a === undefined ? null : a) === JSON.stringify(b === undefined ? null : b);
  // Split the two things the screen was conflating: how much of the form is
  // populated, and how much of it this respondent actually supplied.
  const populatedFields = respondFields.filter((f) => !isEmpty(answers[f.key]));
  const newFields = populatedFields.filter((f) => !sameAsFile(answers[f.key], baseline[f.key]));
  const answeredCount = populatedFields.length;
  const newCount = newFields.length;
  const prefilledCount = answeredCount - newCount;

  const saveDraft = () => {
    if (!respAppId) return;
    const drafts = readLocal(LS_DRAFTS, {}) || {};
    drafts[respAppId] = { answers, at: new Date().toISOString() };
    writeLocal(LS_DRAFTS, drafts);
    setMsg('Draft saved on this device. You can come back and finish later.');
  };

  const submitAnswers = async () => {
    if (!respondApp) return;
    setBusy(true); setErr(''); setMsg('');
    try {
      const fresh = (await store.getFactSheet(respAppId)) || respondApp;
      // Only what changed. Writing back an untouched prefilled value produces
      // an audit entry claiming an edit that never happened.
      const patch = {};
      newFields.forEach((f) => { patch[f.key] = answers[f.key]; });
      await store.saveFactSheet({ ...fresh, fields: { ...(fresh.fields || {}), ...patch } });

      const drafts = readLocal(LS_DRAFTS, {}) || {};
      delete drafts[respAppId];
      writeLocal(LS_DRAFTS, drafts);

      if (respondCampaign) {
        const record = {
          campaignId: respondCampaign.id,
          email: respondent,
          name: respondent,
          appId: respAppId,
          appName: respondApp.name,
          sections: respSections,
          answered: newCount,          // what this respondent supplied, not what was prefilled
          prefilled: prefilledCount,   // already on file and left as-is
          at: new Date().toISOString(),
        };
        // Local-mode-only bookkeeping — API-mode campaigns track completion
        // through survey_assignment (see markMyPartDone below), not through
        // a stored answer record; V1 never writes answers server-side (the
        // fields ARE the answers, already on the fact sheet).
        if (localMode) {
          const next = campaigns.map((c) => (c.id === respondCampaign.id
            ? { ...c, responses: (c.responses || []).filter((r) => !(r.appId === respAppId && r.email === respondent)).concat([record]) }
            : c));
          setCampaigns(next);
          saveLocalCampaigns(next);
        }
      }
      setMsg(`Thank you — ${newCount} answer(s) saved to ${respondApp.name}`
        + (prefilledCount ? `. ${prefilledCount} field(s) already held the same value and were left alone.` : '.'));
      await load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  // The assignee marking THEIR OWN assignment done — matched server-side to
  // the signed-in session's email, never to whatever is typed in "Responding
  // as" (that field is a preview convenience; it does not authenticate you
  // as anyone). V1 does not write answers here — the assignee already
  // updated the fact sheets above, or through the normal screens; this only
  // tells the campaign that this person's part is finished.
  const markMyPartDone = async () => {
    if (!respondCampaign || localMode) return;
    setBusy(true); setErr(''); setMsg('');
    try {
      await api('/surveys/respond', { method: 'PUT', body: JSON.stringify({ campaignId: respondCampaign.id }) });
      setMsg('Marked your part of this campaign as done.');
      await load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const setAnswer = (key, value) => setAnswers((a) => ({ ...a, [key]: value }));

  // ---- Outstanding tab -----------------------------------------------------
  const outstanding = useMemo(() => {
    const groups = new Map();
    apps.forEach((app) => {
      const missing = MANDATORY_FIELDS.filter((f) => isEmpty(app.fields ? app.fields[f.key] : undefined));
      if (!missing.length) return;
      const rawOwner = OWNER_FIELDS.map((o) => (app.fields || {})[o.key]).find((v) => String(v || '').trim());
      const ownerName = String(rawOwner || '').trim() || 'Unassigned — no owner on the fact sheet';
      const k = ownerName.toLowerCase();
      const g = groups.get(k) || {
        key: k,
        name: isEmail(ownerName) ? ownerName.split('@')[0] : ownerName,
        email: isEmail(ownerName) ? ownerName : '',
        apps: [],
        missingCount: 0,
      };
      g.apps.push({ id: app.id, name: app.name, completion: app.completion || 0, missing: missing.map((f) => f.label) });
      g.missingCount += missing.length;
      groups.set(k, g);
    });
    const rows = Array.from(groups.values());
    rows.sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      if (sortBy === 'name') return dir * a.name.localeCompare(b.name);
      if (sortBy === 'apps') return dir * (a.apps.length - b.apps.length);
      return dir * (a.missingCount - b.missingCount);
    });
    return rows;
  }, [apps, sortBy, sortDir]);

  const totalMissing = outstanding.reduce((n, o) => n + o.missingCount, 0);

  const sortHead = (key, label, align) => (
    <TableCell align={align}>
      <TableSortLabel
        active={sortBy === key}
        direction={sortBy === key ? sortDir : 'desc'}
        onClick={() => {
          if (sortBy === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
          else { setSortBy(key); setSortDir(key === 'name' ? 'asc' : 'desc'); }
        }}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  // ---- Friendly field renderer (Respond tab) -------------------------------
  const renderQuestion = (f) => {
    const v = answers[f.key];
    const common = {
      fullWidth: true,
      size: 'small',
      value: v === undefined || v === null ? '' : v,
      onChange: (e) => setAnswer(f.key, e.target.value),
    };
    switch (f.type) {
      case FIELD_TYPES.TEXTAREA:
        return <TextField {...common} multiline minRows={3} placeholder="Type your answer…" />;
      case FIELD_TYPES.SELECT:
      case FIELD_TYPES.RATING:
      case FIELD_TYPES.ARROWS:
        return (
          <TextField {...common} select>
            <MenuItem value=""><em>Not sure / leave blank</em></MenuItem>
            {resolvePicklist(f.picklist).map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </TextField>
        );
      case FIELD_TYPES.MULTISELECT:
        return (
          <TextField
            fullWidth size="small" select
            SelectProps={{ multiple: true }}
            value={Array.isArray(v) ? v : []}
            onChange={(e) => setAnswer(f.key, e.target.value)}
          >
            {resolvePicklist(f.picklist).map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </TextField>
        );
      case FIELD_TYPES.BOOLEAN:
        return (
          <ToggleButtonGroup
            exclusive size="small"
            value={v === true ? 'yes' : v === false ? 'no' : null}
            onChange={(e, next) => setAnswer(f.key, next === 'yes' ? true : next === 'no' ? false : '')}
          >
            <ToggleButton value="yes" sx={{ px: 3 }}>Yes</ToggleButton>
            <ToggleButton value="no" sx={{ px: 3 }}>No</ToggleButton>
          </ToggleButtonGroup>
        );
      case FIELD_TYPES.NUMBER:
      case FIELD_TYPES.CURRENCY:
      case FIELD_TYPES.PERCENT:
        return <TextField {...common} type="number" onChange={(e) => setAnswer(f.key, e.target.value === '' ? '' : Number(e.target.value))} />;
      case FIELD_TYPES.DATE:
        return <TextField {...common} type="date" InputLabelProps={{ shrink: true }} />;
      default:
        return <TextField {...common} placeholder="Type your answer…" />;
    }
  };

  // ---- Render --------------------------------------------------------------
  // Computed once per campaign list rather than inside the row map, where it
  // re-ran on every unrelated state change.
  const statsByCampaign = useMemo(() => {
    const out = {};
    campaigns.forEach((c) => { out[c.id] = statsFor(c); });
    return out;
  }, [campaigns, statsFor]);

  const respondPool = (respondCampaign && respondCampaign.appIds.length
    ? respondCampaign.appIds.map((id) => appById[id]).filter(Boolean)
    : apps);
  const respondOptions = respondPool.slice(0, PICKER_CAP);
  const respondOptionsTruncated = respondPool.length > PICKER_CAP;

  const dialogFields = dialog ? fieldsForSections(dialog.sections) : [];
  // Keyed on the id LIST, not on `dialog`: setDraft returns a new dialog object
  // for every keystroke in the campaign name, which invalidated this memo each
  // time and re-ran an O(n*k) `includes` scan — ~16M comparisons after "Add
  // matching apps". Also a Set, so the scan is O(n) rather than O(n*k).
  const dialogAppIdKey = dialog ? (dialog.appIds || []).join(',') : '';
  const selectedApps = useMemo(
    () => {
      if (!dialog) return [];
      const want = new Set(dialog.appIds || []);
      return apps.filter((a) => want.has(a.id));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dialogAppIdKey, apps]
  );
  const availableUserOptions = dialog
    ? tenantUserOptions.filter((o) => !(dialog.recipients || []).some((r) => r.key === o.key))
    : [];

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: HEAD }}>Surveys</Typography>
        <HelpBubble topic="surveyWorkflow" />
        <Chip size="small" label={`${apps.length} applications`} sx={{ height: 20 }} />
        <Box sx={{ flex: 1 }} />
        <IconButton onClick={load} title="Refresh"><RefreshIcon /></IconButton>
        <Button
          startIcon={<AddIcon />} variant="contained" onClick={openDialog}
          sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}
        >
          New campaign
        </Button>
      </Stack>
      <Typography variant="body2" sx={{ color: GREY, mb: 2 }}>
        Ask assignees only what is missing, only in their area — and land the answers straight on the fact sheet.
      </Typography>

      {localMode && (
        <Alert severity="info" sx={{ mb: 2 }}>
          The <code>/surveys</code> API is not reachable, so campaigns, recipients and notifications are recorded
          in this browser only — you still contact people yourself and enter their answers here. Everything else
          works: the response form writes straight to the fact sheets, and the outstanding list is live against
          tenant data.
        </Alert>
      )}
      {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{err}</Alert>}
      {msg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg('')}>{msg}</Alert>}

      {notifyResults && (
        <Alert
          severity={notifyResults.results.some((r) => r.status === 'failed') ? 'warning' : 'info'}
          sx={{ mb: 2 }}
          onClose={() => setNotifyResults(null)}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Notification results for "{notifyResults.campaignName}"
          </Typography>
          <Stack spacing={0.25}>
            {notifyResults.results.map((r) => (
              <Typography key={r.email} variant="caption" sx={{ display: 'block' }}>
                {r.email} — <b>{r.status}</b>{r.reason ? ` (${r.reason})` : ''}
              </Typography>
            ))}
            {!notifyResults.results.length && (
              <Typography variant="caption">Nobody was eligible — everyone was already notified in the last 24h, or nobody is pending.</Typography>
            )}
          </Stack>
        </Alert>
      )}

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2, borderBottom: '1px solid #E0E6ED' }}>
        <Tab label={<TabLabel text="Campaigns" topic="tabCampaigns" />} sx={{ fontWeight: 600 }} />
        <Tab label={<TabLabel text="Respond (preview)" topic="tabRespondPreview" />} sx={{ fontWeight: 600 }} />
        <Tab label={<TabLabel text={`Outstanding${totalMissing ? ` (${totalMissing})` : ''}`} topic="tabOutstanding" />} sx={{ fontWeight: 600 }} />
      </Tabs>

      {loading && <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={26} /></Box>}

      {/* ------------------------------ TAB 1 ------------------------------ */}
      {!loading && tab === 0 && (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow sx={headSx}>
                <TableCell>Campaign</TableCell>
                <TableCell align="right">Apps</TableCell>
                <TableCell align="right">Questions</TableCell>
                <TableCell align="right">Recipients</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Due</TableCell>
                <TableCell align="right">{localMode ? 'Responses' : 'Done'}</TableCell>
                <TableCell>Complete</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {campaigns.map((c) => {
                const s = statsByCampaign[c.id];
                return (
                  <TableRow key={c.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" sx={{ fontWeight: 600, color: HEAD }}>{c.name}</Typography>
                        {c.status && (
                          <Chip
                            size="small" label={c.status}
                            sx={{ height: 18, bgcolor: STATUS_COLOR[c.status] || GREY, color: '#fff' }}
                          />
                        )}
                      </Stack>
                      <SectionChips sections={c.sections || []} />
                    </TableCell>
                    <TableCell align="right">{(c.appIds || []).length}</TableCell>
                    <TableCell align="right">{s.questions}</TableCell>
                    <TableCell align="right">{s.recipients}</TableCell>
                    <TableCell>{shortDate(c.createdAt)}</TableCell>
                    <TableCell>{c.dueDate || '—'}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{s.received} in</Typography>
                      <Typography variant="caption" sx={{ color: s.outstanding ? '#B26A00' : '#2E7D32' }}>
                        {s.outstanding} outstanding
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Progress value={s.responsePct} />
                      <Tooltip title="Share of the scoped questions that now have an answer on the fact sheets">
                        <Typography variant="caption" sx={{ color: GREY }}>
                          {s.dataPct}% of the {s.questions} fields this campaign asks about are captured
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      {!localMode && (
                        <Tooltip title="Edit this campaign's scope and recipients">
                          <IconButton size="small" onClick={() => openEditDialog(c)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip
                        title={localMode
                          ? 'List everyone still outstanding. No email is sent — the survey API is not reachable.'
                          : 'Email everyone outstanding who has not been notified in the last 24h (confirmation required)'}
                      >
                        <span>
                          <IconButton size="small" onClick={() => notifyIconClick(c)} disabled={!s.outstanding}>
                            <NotificationsActiveIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Preview what the recipient sees">
                        <IconButton
                          size="small"
                          onClick={() => { setRespCampaignId(c.id); setTab(1); }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!campaigns.length && (
                <TableRow>
                  <TableCell colSpan={9} sx={{ color: '#9AA6B2', textAlign: 'center', py: 4 }}>
                    No campaigns yet. Create one to stop chasing owners by hand.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* ------------------------------ TAB 2 ------------------------------ */}
      {!loading && tab === 1 && (
        <Box>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                select size="small" label="Campaign (optional)" sx={{ minWidth: 220 }}
                value={respCampaignId} onChange={(e) => setRespCampaignId(e.target.value)}
              >
                <MenuItem value=""><em>None — ad-hoc preview</em></MenuItem>
                {campaigns.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </TextField>
              <TextField
                select size="small" label="Application" sx={{ minWidth: 240 }}
                value={respAppId} onChange={(e) => setRespAppId(e.target.value)}
              >
                <MenuItem value=""><em>Choose an application</em></MenuItem>
                {/* Capped like AiEnrichment's ROW_CAP and SavedViews' slice(0,300):
                    a MUI Select mounts every MenuItem, and a real tenant has
                    thousands. A campaign's own list is already bounded. */}
                {respondOptions.map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
                {respondOptionsTruncated && (
                  <MenuItem disabled>
                    <em>
                      Showing the first {PICKER_CAP.toLocaleString()} of {apps.length.toLocaleString()} —
                      choose a campaign to narrow the list
                    </em>
                  </MenuItem>
                )}
              </TextField>
              <Autocomplete
                multiple size="small" options={SECTION_NAMES} value={respSections}
                onChange={(e, v) => setRespSections(v)} sx={{ flex: 1, minWidth: 260 }}
                renderInput={(params) => <TextField {...params} label="Sections in scope" />}
              />
              <TextField
                size="small" label="Responding as" sx={{ minWidth: 200 }}
                value={respondent} onChange={(e) => setRespondent(e.target.value)}
              />
            </Stack>
          </Paper>

          {!respondApp || !respSections.length ? (
            <Paper sx={{ p: 4, textAlign: 'center', color: '#9AA6B2' }}>
              Pick an application and at least one section to see the owner's questionnaire.
            </Paper>
          ) : (
            <Paper sx={{ p: 0, maxWidth: 900 }}>
              <Box sx={{ p: 3, bgcolor: NAVY, color: '#fff' }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {respondCampaign ? respondCampaign.name : 'Application discovery'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5 }}>
                  About <b>{respondApp.name}</b>
                  {respondCampaign && respondCampaign.dueDate ? ` — please reply by ${respondCampaign.dueDate}` : ''}
                </Typography>
              </Box>
              <Box sx={{ px: 3, py: 2, bgcolor: '#F5F8FB' }}>
                <Typography variant="body2" sx={{ color: GREY, mb: 1.5 }}>
                  {DEFAULT_MESSAGE}
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Progress value={pct(answeredCount, respondFields.length)} width={200} />
                  <Typography variant="caption" sx={{ color: GREY }}>
                    {answeredCount} of {respondFields.length} populated
                    {prefilledCount > 0 && ` · ${newCount} new, ${prefilledCount} already on file`}
                  </Typography>
                </Stack>
              </Box>
              <Divider />
              <Box sx={{ p: 3 }}>
                {respSections.map((section) => {
                  const fields = respondFields.filter((f) => f.section === section);
                  if (!fields.length) return null;
                  return (
                    <Box key={section} sx={{ mb: 4 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: HEAD, mb: 2 }}>
                        {section}
                      </Typography>
                      <Stack spacing={3}>
                        {fields.map((f) => (
                          <Box key={f.key}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="body2" sx={{ fontWeight: 600, color: HEAD }}>
                                {f.label}{f.mandatory ? ' *' : ''}
                              </Typography>
                              {!isEmpty(answers[f.key]) && <CheckCircleIcon sx={{ fontSize: 15, color: '#2E7D32' }} />}
                            </Stack>
                            <Typography variant="caption" sx={{ color: GREY, display: 'block', mb: 1 }}>
                              {helperFor(f)}
                            </Typography>
                            {renderQuestion(f)}
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  );
                })}
              </Box>
              <Divider />
              <Stack direction="row" spacing={2} sx={{ p: 2 }} justifyContent="flex-end" alignItems="center">
                {respondCampaign && !localMode && (
                  <Tooltip title={me && me.email ? `Marks ${me.email}'s own assignment on this campaign done — never anyone else's.` : ''}>
                    <span>
                      <Button startIcon={<CheckCircleIcon />} onClick={markMyPartDone} disabled={busy}>
                        Mark my part done
                      </Button>
                    </span>
                  </Tooltip>
                )}
                <Button startIcon={<SaveIcon />} onClick={saveDraft} disabled={busy}>Save draft</Button>
                <Button
                  variant="contained" startIcon={<SendIcon />} onClick={submitAnswers} disabled={busy}
                  sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}
                >
                  {busy ? 'Submitting…' : 'Submit answers'}
                </Button>
              </Stack>
            </Paper>
          )}
        </Box>
      )}

      {/* ------------------------------ TAB 3 ------------------------------ */}
      {!loading && tab === 2 && (
        <Box>
          <Typography variant="body2" sx={{ color: GREY, mb: 2 }}>
            {totalMissing} mandatory answer(s) still outstanding across {outstanding.reduce((n, o) => n + o.apps.length, 0)} application(s),
            grouped by the person who owns them.
          </Typography>
          <Paper sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={headSx}>
                  {sortHead('name', 'Owner')}
                  {sortHead('apps', 'Applications', 'right')}
                  {sortHead('missing', 'Missing answers', 'right')}
                  <TableCell align="right">Chase</TableCell>
                </TableRow>
              </TableHead>
            </Table>
          </Paper>

          {outstanding.map((o) => (
            <Accordion key={o.key} disableGutters sx={{ mb: 0.5 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', pr: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: HEAD, minWidth: 220 }}>{o.name}</Typography>
                  <Chip
                    size="small" sx={{ height: 20 }}
                    label={o.email || 'no email on file'}
                    color={o.email ? 'default' : 'warning'}
                    variant="outlined"
                  />
                  <Box sx={{ flex: 1 }} />
                  <Typography variant="caption" sx={{ color: GREY }}>{o.apps.length} app(s)</Typography>
                  <Chip size="small" label={`${o.missingCount} missing`} sx={{ height: 20, bgcolor: '#FDECEA', color: '#B3261E' }} />
                  <Tooltip title="Show what this owner still owes so you can chase them yourself. No email is sent from here.">
                    <Button
                      size="small" startIcon={<NotificationsActiveIcon />}
                      onClick={(e) => { e.stopPropagation(); remindOwner(o); }}
                      sx={{ color: BLUE }}
                    >
                      Chase list
                    </Button>
                  </Tooltip>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ bgcolor: '#F9FBFD' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={headSx}>
                      <TableCell>Application</TableCell>
                      <TableCell>Completion</TableCell>
                      <TableCell>Still needed</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {o.apps
                      .slice()
                      .sort((a, b) => b.missing.length - a.missing.length)
                      .map((a) => (
                        <TableRow key={a.id} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{a.name}</TableCell>
                          <TableCell sx={{ width: 190 }}><Progress value={a.completion} width={110} /></TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {a.missing.map((m) => (
                                <Chip key={m} size="small" label={m} variant="outlined" sx={{ height: 20 }} />
                              ))}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </AccordionDetails>
            </Accordion>
          ))}

          {!outstanding.length && (
            <Paper sx={{ p: 4, textAlign: 'center', color: '#9AA6B2' }}>
              Nothing outstanding — every application has its mandatory fields.
            </Paper>
          )}
        </Box>
      )}

      {/* --------------------------- CREATE/EDIT DIALOG -------------------------- */}
      <Dialog open={!!dialog} onClose={() => !busy && setDialog(null)} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 700, color: HEAD }}>
          {dialog && dialog.apiId ? 'Edit survey campaign' : 'New survey campaign'}
        </DialogTitle>
        {dialog && (
          <DialogContent dividers>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
              <TextField
                fullWidth size="small" label="Campaign name" value={dialog.name}
                onChange={(e) => setDraft({ name: e.target.value })}
                placeholder="e.g. Wave 1 — DR & business continuity"
              />
              <TextField
                size="small" type="date" label="Due date" InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 180 }} value={dialog.dueDate}
                onChange={(e) => setDraft({ dueDate: e.target.value })}
              />
              <TextField
                select size="small" label="Status" sx={{ minWidth: 140 }} value={dialog.status}
                onChange={(e) => setDraft({ status: e.target.value })}
              >
                {STATUS_OPTIONS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Stack>
            {!localMode && (
              <Typography variant="caption" sx={{ color: GREY, display: 'block', mb: 2 }}>
                Only an <b>open</b> campaign can have notifications sent.
              </Typography>
            )}

            {/* 1. Applications */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: HEAD, mb: 1 }}>
              1. Which applications? ({dialog.appIds.length} selected)
            </Typography>
            <Autocomplete
              multiple size="small" options={apps} value={selectedApps}
              // MUI filters the full list on each keystroke but only renders the
              // matches; the cap keeps the initial open bounded on a large tenant.
              filterOptions={(opts, state) => {
                const q = String(state.inputValue || '').toLowerCase();
                const hits = q ? opts.filter((o) => String(o.name || '').toLowerCase().includes(q)) : opts;
                return hits.slice(0, PICKER_CAP);
              }}
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              onChange={(e, v) => setDraft({ appIds: v.map((a) => a.id) })}
              renderInput={(params) => <TextField {...params} label="Search applications" placeholder="Type to search…" />}
              sx={{ mb: 1 }}
            />
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
              <TextField
                select size="small" label="…or all apps missing" sx={{ minWidth: 280 }}
                value={missingFieldKey} onChange={(e) => setMissingFieldKey(e.target.value)}
              >
                {UNIQUE_FIELDS.slice().sort((a, b) => a.label.localeCompare(b.label)).map((f) => (
                  <MenuItem key={f.key} value={f.key}>{f.label}</MenuItem>
                ))}
              </TextField>
              <Button size="small" onClick={addAppsMissingField} disabled={!missingFieldKey} sx={{ color: BLUE }}>
                Add matching apps
              </Button>
              {dialog.appIds.length > 0 && (
                <Button size="small" onClick={() => setDraft({ appIds: [], recipients: [] })} sx={{ color: GREY }}>
                  Clear
                </Button>
              )}
            </Stack>

            {/* 2. Sections */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: HEAD, mb: 0.5 }}>
              2. Which sections should we ask about?
            </Typography>
            <Typography variant="caption" sx={{ color: GREY, display: 'block', mb: 1 }}>
              Scoping is what keeps a survey short enough to get answered — {dialogFields.length} question(s) so far.
            </Typography>
            <Box sx={{ mb: 3, maxHeight: 190, overflowY: 'auto', border: '1px solid #E0E6ED', borderRadius: 1, p: 1 }}>
              {SECTION_NAMES.map((s) => (
                <FormControlLabel
                  key={s}
                  sx={{ display: 'block', ml: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={dialog.sections.includes(s)}
                      onChange={(e) => setDraft({
                        sections: e.target.checked
                          ? dialog.sections.concat([s])
                          : dialog.sections.filter((x) => x !== s),
                      })}
                    />
                  }
                  label={<span style={{ fontSize: 13 }}>{s} <span style={{ color: GREY }}>({SECTION_SIZE[s]} questions)</span></span>}
                />
              ))}
            </Box>

            {/* 3. Recipients */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: HEAD, mb: 0.5 }}>
              3. Who do we ask? ({dialog.recipients.length} selected)
            </Typography>
            <Typography variant="caption" sx={{ color: GREY, display: 'block', mb: 1 }}>
              V1 can only notify an EXISTING Primordia user of this workspace — search for one below, or pick
              from the owners suggested from the selected applications.
            </Typography>
            <Autocomplete
              size="small"
              options={availableUserOptions}
              getOptionLabel={(o) => `${o.label} <${o.email}>`}
              onChange={(e, v) => addExistingUser(v)}
              value={null}
              renderInput={(params) => <TextField {...params} label="Add an existing Primordia user" placeholder="Search this workspace's users…" />}
              sx={{ mb: 1 }}
            />
            {!tenantUserOptions.length && (
              <Typography variant="caption" sx={{ color: '#9AA6B2', display: 'block', mb: 1 }}>
                No user list is available to you here — add recipients by email below instead; V1 can only
                notify someone who already has an enabled Primordia account in this workspace, so an address
                that does not match one will be kept with the campaign but skipped when sending.
              </Typography>
            )}
            <Box sx={{ mb: 1, maxHeight: 220, overflowY: 'auto', border: '1px solid #E0E6ED', borderRadius: 1, p: 1 }}>
              {!suggestedRecipients.length && (
                <Typography variant="caption" sx={{ color: '#9AA6B2' }}>
                  {dialog.appIds.length
                    ? 'No owners recorded on these applications — add recipients above or manually below.'
                    : 'Select applications to see suggested owners.'}
                </Typography>
              )}
              {suggestedRecipients.map((s) => {
                const selected = dialog.recipients.some((r) => r.key === s.key);
                const email = emailEdits[s.key] !== undefined ? emailEdits[s.key] : s.email;
                return (
                  <Stack key={s.key} direction="row" spacing={1} alignItems="center" sx={{ py: 0.5 }}>
                    <Checkbox size="small" checked={selected} onChange={() => toggleRecipient(s)} />
                    <Box sx={{ minWidth: 200 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.name}</Typography>
                      <Typography variant="caption" sx={{ color: GREY }}>
                        {s.roles.join(', ')} — {s.appIds.length} app(s)
                      </Typography>
                    </Box>
                    <TextField
                      size="small" placeholder="email address" sx={{ flex: 1 }}
                      value={email} onChange={(e) => setRecipientEmail(s.key, e.target.value)}
                      error={!!email && !isEmail(email)}
                    />
                  </Stack>
                );
              })}
            </Box>
            <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
              <TextField
                size="small" fullWidth label="Add a recipient by email"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { addManualRecipient(e.target.value); e.target.value = ''; e.preventDefault(); }
                }}
                helperText="Press Enter to add. Only an address with an existing, enabled Primordia account in this workspace will actually be notified — others are kept with the campaign but skipped when sending."
              />
            </Stack>
            {!!dialog.recipients.length && (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
                {dialog.recipients.map((r) => (
                  <Chip
                    key={r.key} size="small" label={r.email || `${r.name} (no email)`}
                    onDelete={() => setDraft({ recipients: dialog.recipients.filter((x) => x.key !== r.key) })}
                    color={r.email ? 'default' : 'warning'}
                  />
                ))}
              </Stack>
            )}
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setDialog(null)} disabled={busy}>Cancel</Button>
          <Button
            variant="contained" startIcon={localMode ? <SaveIcon /> : <SendIcon />} onClick={saveCampaign}
            disabled={busy || !dialog || !dialog.appIds.length || !dialog.sections.length || !dialog.recipients.length}
            sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}
          >
            {busy && 'Saving…'}
            {!busy && (dialog && dialog.apiId ? 'Save changes' : (localMode ? 'Create campaign' : 'Create & assign'))}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------- SEND NOTIFICATIONS CONFIRM ------------------------- */}
      <Dialog open={!!sendDialog} onClose={() => !sendBusy && setSendDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700, color: HEAD }}>Send survey notifications</DialogTitle>
        {sendDialog && (
          <DialogContent dividers>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This will email up to <b>{sendDialog.campaign.counts ? sendDialog.campaign.counts.pending : '?'}</b> pending
              recipient(s) of "<b>{sendDialog.campaign.name}</b>" who have not been notified in the last 24 hours
              (a hard cap of 50 recipients applies per send). Recipients with no enabled Primordia account in this
              workspace are skipped automatically.
            </Alert>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Type <b>SEND</b> to confirm.
            </Typography>
            <TextField
              fullWidth size="small" autoFocus placeholder="SEND"
              value={sendDialog.confirmText}
              onChange={(e) => setSendDialog((d) => ({ ...d, confirmText: e.target.value }))}
            />
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setSendDialog(null)} disabled={sendBusy}>Cancel</Button>
          <Button
            variant="contained" color="warning" startIcon={<SendIcon />}
            onClick={confirmSend}
            disabled={sendBusy || !sendDialog || sendDialog.confirmText.trim().toUpperCase() !== 'SEND'}
          >
            {sendBusy ? 'Sending…' : 'Send notifications'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
